import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { WipeAwayForegroundMedia } from "../WipeAwayForegroundMedia/WipeAwayForegroundMedia";

type WipeAwayForegroundProps = {
  media?: string;
  brushRadiusMultipler: number;
};

type Point = {
  x: number;
  y: number;
};

export function WipeAwayForeground({
  media,
  brushRadiusMultipler,
}: WipeAwayForegroundProps) {
  const [brushRadius, setBrushRadius] = useState<number>(50);
  const [canvasWidth, setCanvasWidth] = useState<number>(0);
  const [canvasHeight, setCanvasHeight] = useState<number>(0);
  const ref = useRef<HTMLCanvasElement>(null);
  const refDots = useRef<Point[]>([]);

  const getBrushPos = useCallback((xRef: number, yRef: number): Point => {
    if (!ref.current) {
      return { x: 0, y: 0 };
    }

    const bridgeRect = ref.current.getBoundingClientRect();
    return {
      x: Math.floor(
        ((xRef - bridgeRect.left) / (bridgeRect.right - bridgeRect.left)) *
          ref.current.width,
      ),
      y: Math.floor(
        ((yRef - bridgeRect.top) / (bridgeRect.bottom - bridgeRect.top)) *
          ref.current.height,
      ),
    };
  }, []);

  const drawDot = useCallback(
    (point: Point) => {
      if (!ref.current) {
        return;
      }

      const context = ref.current.getContext("2d");
      if (!context) {
        return;
      }

      const fillStyle = context.fillStyle;
      const globalCompositeOperation = context.globalCompositeOperation;

      context.fillStyle = "#000";
      context.globalCompositeOperation = "destination-out";

      context.beginPath();
      context.arc(point.x, point.y, brushRadius, 0, 2 * Math.PI, true);
      context.fill();

      context.fillStyle = fillStyle;
      context.globalCompositeOperation = globalCompositeOperation;
    },
    [brushRadius],
  );

  const drawDots = useCallback(() => {
    if (!ref.current) {
      return;
    }

    const context = ref.current.getContext("2d");
    if (!context) {
      return;
    }

    refDots.current.forEach((dot) => drawDot(dot));
  }, [drawDot]);

  const onMouseMove = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const dot = getBrushPos(event.screenX, event.screenY);
      refDots.current.push(dot);
      drawDot(dot);
    },
    [drawDot, getBrushPos],
  );

  const onTouchMove = useCallback(
    (event: React.TouchEvent<HTMLCanvasElement>) => {
      for (let i = 0; i < event.touches.length; i++) {
        const dot = getBrushPos(
          event.touches[i].screenX,
          event.touches[i].screenY,
        );
        refDots.current.push(dot);
        drawDot(dot);
      }
    },
    [drawDot, getBrushPos],
  );

  const onMediaElementRendered = useCallback(
    (element: HTMLVideoElement | HTMLImageElement) => {
      if (!ref.current) {
        return;
      }

      const context = ref.current.getContext("2d");
      if (!context) {
        return;
      }

      context.drawImage(element, 0, 0, canvasWidth, canvasHeight);

      drawDots();
    },
    [canvasHeight, canvasWidth, drawDots],
  );

  useLayoutEffect(() => {
    const updateCanvasSizing = () => {
      if (ref.current) {
        setCanvasWidth(ref.current.clientWidth);
        setCanvasHeight(ref.current.clientHeight);
        setBrushRadius((ref.current.clientWidth / 100) * brushRadiusMultipler);
      }
    };

    updateCanvasSizing();

    window.addEventListener("resize", updateCanvasSizing);
    return () => {
      window.removeEventListener("resize", updateCanvasSizing);
    };
  }, [brushRadiusMultipler]);

  return (
    <WipeAwayForegroundMedia
      media={media}
      onMediaElementRendered={onMediaElementRendered}
    >
      <canvas
        key={media}
        ref={ref}
        className="absolute top-0 left-0 w-full h-full"
        width={canvasWidth}
        height={canvasHeight}
        onMouseMove={onMouseMove}
        onTouchMove={onTouchMove}
      />
    </WipeAwayForegroundMedia>
  );
}
